import { fetchAndRefresh } from '@teqplay/web-sdk/rest'
import type { Dispatch, AnyAction } from 'redux'

import { loadAuth, saveAuth, clearAuth as clearStoredAuth } from './authStorage'
import type { AuthData } from './authStorage'
import { BASE_URL } from '../js/constants'
import { setNewToken, clearAuth as clearAuthAction } from '../pages/login/actions'

/**
 * Error object augmented with an optional HTTP status code.
 */
type HttpError = Error & { status?: number }

/**
 * Minimal shape of auth updates we expect from fetchAndRefresh's onChangeAuth callback.
 */
type NextAuth = {
  username?: string | null
  terminalId?: string | number | null
  token: string
  refreshToken?: string | null
}

function isHttpError(e: unknown): e is HttpError {
  return typeof e === 'object' && e !== null && 'status' in e
}

let reduxStore: { dispatch: Dispatch<AnyAction> } | undefined
export function setReduxStore(store: { dispatch: Dispatch<AnyAction> }) {
  reduxStore = store
}

class ApiClient {
  private static _instance: ApiClient | null = null
  private baseURL: string = BASE_URL

  static get instance(): ApiClient {
    if (!ApiClient._instance) {
      ApiClient._instance = new ApiClient()
    }
    return ApiClient._instance
  }

  private buildUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
    const slash = pathOrUrl.startsWith('/') ? '' : '/'
    return `${this.baseURL}${slash}${pathOrUrl}`
  }

  private async request<TResponse = unknown, TBody = unknown>(
    method: string,
    pathOrUrl: string,
    body?: TBody,
    init?: RequestInit
  ): Promise<TResponse> {
    const url = this.buildUrl(pathOrUrl)
    const auth: AuthData | null = loadAuth()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init?.headers as Record<string, string>)
    }
    if (auth?.token) headers['Authorization'] = auth.token

    const options: RequestInit = {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...init
    }

    return fetchAndRefresh({
      url,
      options,
      projectURL: this.baseURL + '/v1',
      auth,
      onChangeAuth: (next: NextAuth) => {
        // Update storage
        saveAuth({
          username: next.username ?? null,
          terminalId: next.terminalId ?? null,
          token: next.token ?? null,
          refreshToken: next.refreshToken ?? null
        })
        // Update Redux for backward compatibility (if store attached)
        try {
          reduxStore?.dispatch(setNewToken(next.token, next.refreshToken ?? null))
        } catch (err) {
          console.error(err)
        }
      },
      customResolver: async (response: Response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          const error: HttpError = new Error(text || response.statusText)
          error.status = response.status
          throw error
        }
        const ct = response.headers.get('content-type') || ''
        if (ct.includes('application/json')) return response.json()
        if (ct.includes('text/')) return response.text()
        return response.blob()
      }
    }).catch((err: unknown) => {
      if (isHttpError(err) && err.status === 401) {
        // Hard unauthorized: clear storage and redux
        clearStoredAuth()
        try {
          reduxStore?.dispatch(clearAuthAction())
        } catch (err) {
          console.error(err)
        }
      }
      throw err
    })
  }

  get<T = unknown>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('GET', path, undefined, init)
  }
  post<T = unknown, B = unknown>(path: string, body?: B, init?: RequestInit): Promise<T> {
    return this.request<T, B>('POST', path, body, init)
  }
  put<T = unknown, B = unknown>(path: string, body?: B, init?: RequestInit): Promise<T> {
    return this.request<T, B>('PUT', path, body, init)
  }
  delete<T = unknown>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, init)
  }
}

export const api = ApiClient.instance
